import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FACE_MATCH_THRESHOLD_COSINE,
  FACE_MATCH_TIMEOUT_MS,
  FACE_PHOTO_JPEG_QUALITY,
} from '@/shared/config/terminal.config';
import {
  SCAN_QUALITY_THRESHOLDS,
  QUALITY_GATE_TIMEOUT_MS,
  getQualityHint,
  confirmLivenessDirection,
  pickRandomDirection,
} from '@/shared/config/quality.config';
import type { LivenessDirection, LivenessMode, QualityThresholds } from '@/shared/config/quality.config';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import type { FaceEmbedding } from '../../domain/valueObjects/FaceEmbedding';
import type { ImageFrame } from '../../domain/types/ImageFrame';
import type { QualityRuntimeConfig } from '../../infrastructure/face/types';
import { DataUriPhoto } from '../../domain/valueObjects/DataUriPhoto';
import { getFaceMatchingRepository } from '../../infrastructure/repositories/container';
import { OnnxFaceMatcher } from '../../infrastructure/face/OnnxFaceMatcher';
import type { FaceMatchedEmployee } from '../../domain/entities/FaceMatchResult';
import {
  LivenessAggregator,
  type AggregatorConfig,
} from '../../domain/liveness/LivenessAggregator';
import {
  NoFaceDetectedError,
  MultipleFacesError,
  LowQualityError,
  LivenessBorderlineError,
  ModelLoadError,
  FaceRecognitionError,
} from '../../infrastructure/face/errors';
import { logTiming } from '@/shared/utils/debugLog';
import { nextInterval } from '../../infrastructure/face/pacing';
import { classifyTier, detectionScaleForTier } from '../../infrastructure/face/DevicePerfProfile';

export type ScanPhase =
  | 'idle'
  | 'loading-models'
  | 'camera-prompt'
  | 'camera-ready'
  | 'quality-checking'
  | 'scanning'
  | 'comparing'
  | 'matched'
  | 'error'
  | 'denied';

export interface MatchedScanEmployee extends FaceMatchedEmployee {
  lastFramePhoto: DataUriPhoto;
  matchedBiometricId: number | null;
}

interface UseScanLifecycleParams {
  action: AttendanceAction;
  accessRoleId: number | null;
  /** Set to false when the action does not need facial scanning (e.g. acceso-empleado). */
  enabled: boolean;
  /**
   * When false (VITE_ENABLE_QUALITY_GATE=false), the quality-checking phase is skipped
   * and the scan loop starts immediately after the camera is ready.
   * Defaults to true.
   */
  enableQualityGate?: boolean;
  /** Override quality thresholds (defaults to SCAN_QUALITY_THRESHOLDS). */
  thresholds?: QualityThresholds;
  /**
   * Liveness challenge mode:
   *  'disabled'  — quality gate runs but no direction challenge or lookFront;
   *                scan starts immediately when qualityHint===null.
   *  'minimum'   — 1 direction + lookFront (default / existing behaviour).
   *  'strong'    — 2 sequential directions + lookFront, no intermediate lookFront.
   */
  livenessMode?: LivenessMode;
  /** Quality gate session timeout in ms (defaults to QUALITY_GATE_TIMEOUT_MS). */
  qualityGateTimeoutMs?: number;
  /** Face match threshold for scan loop (defaults to FACE_MATCH_THRESHOLD_COSINE). */
  matchThreshold?: number;
}

interface UseScanLifecycleResult {
  phase: ScanPhase;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  cameraActive: boolean;
  matchedEmployee: MatchedScanEmployee | null;
  /** The last valid frame with a detected face — preserved for auto-enrollment after PIN. */
  lastValidFrameRef: React.MutableRefObject<{ frame: ImageFrame; embedding: FaceEmbedding } | null>;
  error: { code: string; message?: string } | null;
  /** Current quality hint i18n key, or null if quality is acceptable. */
  qualityHint: string | null;
  /** Active liveness direction challenge, or null when not in liveness step. */
  livenessDirection: LivenessDirection | null;
  retry: () => void;
}

/**
 * Picks a random liveness direction that the user's current pose does NOT already satisfy.
 * This prevents trivially picking a direction the user is already facing, ensuring
 * they must actively move to complete the challenge.
 */
function pickDirectionAvoidingPose(
  yaw: number,
  pitch: number,
  thresholds: QualityThresholds,
): LivenessDirection {
  const all: LivenessDirection[] = ['right', 'left', 'up', 'down'];
  const available = all.filter((d) => !confirmLivenessDirection(d, yaw, pitch, thresholds));
  if (available.length === 0) return pickRandomDirection(); // safety fallback (theoretically unreachable)
  return available[Math.floor(Math.random() * available.length)]!;
}

export function useScanLifecycle({
  accessRoleId,
  enabled,
  enableQualityGate = true,
  thresholds:          thresholdsProp,
  livenessMode:        livenessMdProp,
  qualityGateTimeoutMs: gateTimeoutProp,
  matchThreshold:      matchThresholdProp,
}: UseScanLifecycleParams): UseScanLifecycleResult {
  const [phase, setPhase] = useState<ScanPhase>(enabled ? 'loading-models' : 'idle');
  const [cameraActive, setCameraActive] = useState(false);
  const [matchedEmployee, setMatchedEmployee] = useState<MatchedScanEmployee | null>(null);
  const [error, setError] = useState<{ code: string; message?: string } | null>(null);
  const [qualityHint, setQualityHint] = useState<string | null>(null);
  const [livenessDirection, setLivenessDirection] = useState<LivenessDirection | null>(null);

  // Slice A of unify-scan-embedding-liveness: read persisted Gate B settings and
  // build a QualityRuntimeConfig that gets forwarded to BOTH the quality gate
  // (Gate A → matcher.analyzeQuality) AND the scan-loop repo call (Gate B →
  // repo.inferEmbedding) so thresholds stay unified. Mirrors the enroll flow in
  // AdminEnrollFacePage. Mapping: brightnessMinGateB → brightnessMin, etc.
  const {
    blurMinLaplacianVar,
    brightnessMinGateB,
    brightnessMaxGateB,
    faceMinShortSidePx,
    // Slice B of unify-scan-embedding-liveness: aggregator params from persisted
    // settings drive the multi-frame passive-liveness verdict, replacing the
    // legacy FACE_LIVENESS_HIGH/LOW 3-band single-frame classifier.
    passiveHigh,
    passiveLow,
    livenessWindowSize,
    livenessLiveCount,
    livenessSpoofConsecutive,
    livenessMaxTotalFrames,
    frameThrottleFps,
  } = useQualitySettings();
  
  const frameIntervalMs = Math.round(1000 / frameThrottleFps);

  const runtimeConfig: QualityRuntimeConfig = useMemo(() => ({
    blurMinLaplacianVar,
    brightnessMin: brightnessMinGateB,
    brightnessMax: brightnessMaxGateB,
    faceMinShortSidePx,
  }), [blurMinLaplacianVar, brightnessMinGateB, brightnessMaxGateB, faceMinShortSidePx]);

  const aggregatorConfig: AggregatorConfig = useMemo(() => ({
    passiveHigh,
    passiveLow,
    windowSize:       livenessWindowSize,
    liveCount:        livenessLiveCount,
    spoofConsecutive: livenessSpoofConsecutive,
    maxTotalFrames:   livenessMaxTotalFrames,
  }), [passiveHigh, passiveLow, livenessWindowSize, livenessLiveCount, livenessSpoofConsecutive, livenessMaxTotalFrames]);

  const videoRef                = useRef<HTMLVideoElement | null>(null);
  const canvasRef               = useRef<HTMLCanvasElement | null>(null);
  const streamRef               = useRef<MediaStream | null>(null);
  const rafRef                  = useRef<number>(0);
  const timeoutRef              = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const qualityGateTimeoutRef   = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastFrameTimeRef        = useRef<number>(0);
  const lastQualityFrameTimeRef = useRef<number>(0);
  const lastInferenceMsRef      = useRef<number>(0);
  const runningRef              = useRef<boolean>(false);
  const analyzingRef            = useRef<boolean>(false);
  const lastValidFrameRef       = useRef<{ frame: ImageFrame; embedding: FaceEmbedding } | null>(null);
  const retryCounterRef         = useRef<number>(0);
  // Ref kept in sync with the memoised runtimeConfig so the async rAF loops
  // always read the latest persisted values without needing to rebuild the
  // scan/quality-gate callbacks on every settings change.
  const runtimeConfigRef        = useRef<QualityRuntimeConfig>(runtimeConfig);
  useEffect(() => { runtimeConfigRef.current = runtimeConfig; }, [runtimeConfig]);
  /**
   * Slice B of unify-scan-embedding-liveness: LivenessAggregator instance for the
   * current scan session. Reset by `retry()` via `resetAggregator()`. Recreated
   * whenever the persisted aggregator config changes so subsequent scans use the
   * up-to-date passiveHigh/passiveLow/windowSize/… params from useQualitySettings.
   */
  const aggregatorRef = useRef<LivenessAggregator>(new LivenessAggregator(aggregatorConfig));
  useEffect(() => {
    aggregatorRef.current = new LivenessAggregator(aggregatorConfig);
  }, [aggregatorConfig]);
  /** Tracks the active liveness direction inside async rAF loop (avoids stale state closure). */
  const livenessDirectionRef          = useRef<LivenessDirection | null>(null);
  const livenessChallengeStartRef     = useRef<number>(0);
  const livenessDirectionTimerPausedRef = useRef<number | null>(null); // remaining ms when paused due to quality issue

  const cleanup = useCallback(() => {
    runningRef.current = false;
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timeoutRef.current);
    clearTimeout(qualityGateTimeoutRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const captureFrame = useCallback((): ImageFrame | null => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return null;

    let canvas = canvasRef.current;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvasRef.current = canvas;
    }
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return { data: data.data, width: data.width, height: data.height, format: 'rgba' };
  }, []);

  const capturePhotoDataUri = useCallback((): DataUriPhoto | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    try {
      const dataUri = canvas.toDataURL('image/jpeg', FACE_PHOTO_JPEG_QUALITY);
      return DataUriPhoto.from(dataUri);
    } catch {
      return null;
    }
  }, []);

  const startScanLoop = useCallback(() => {
    if (!enabled || accessRoleId === null) return;

    const repo = getFaceMatchingRepository();
    runningRef.current = true;

    // Global timeout
    timeoutRef.current = setTimeout(() => {
      if (runningRef.current) {
        cleanup();
        setPhase('error');
        setError({ code: 'NO_MATCH', message: 'No match found within timeout' });
      }
    }, FACE_MATCH_TIMEOUT_MS);

    const loop = async () => {
      if (!runningRef.current) return;

      const now = performance.now();
      if (now - lastFrameTimeRef.current >= frameIntervalMs) {
        lastFrameTimeRef.current = now;

        const frame = captureFrame();
        if (frame) {
          try {
            const inferStart = performance.now();
            // Slice B of unify-scan-embedding-liveness: consume per-frame
            // liveness score from the repo and drive verdict via
            // LivenessAggregator (multi-frame, config-driven). Replaces the
            // legacy FACE_LIVENESS_HIGH/LOW 3-band single-frame classifier.
            const { embedding, livenessScore } = await repo.inferEmbeddingWithLiveness(
              frame,
              runtimeConfigRef.current,
            );
            const inferMs = performance.now() - inferStart;

            const decision = aggregatorRef.current.push(livenessScore);

            logTiming('[InferEmbedding]', {
              flow:              'auth',
              durationMs:        inferMs,
              ok:                true,
              livenessScore:     livenessScore.toFixed(3),
              aggregatorVerdict: decision.verdict,
            });

            if (decision.verdict === 'pending') {
              // Not enough evidence yet — keep looping without matching.
              setPhase('scanning');
              setError(null);
              lastValidFrameRef.current = { frame, embedding };
              // fallthrough to the next rAF tick below
            } else if (decision.verdict === 'spoof') {
              // Terminal fraud detection — must set phase='error' so ScanningPage
              // navigates to /face-error with the LIVENESS_SPOOF code.
              cleanup();
              setPhase('error');
              setError({ code: 'LIVENESS_SPOOF' });
              return;
            } else if (decision.verdict === 'inconclusive') {
              // Aggregator timed out without a decision — route to auth-error page.
              cleanup();
              setPhase('error');
              setError({ code: 'LIVENESS_INCONCLUSIVE' });
              return;
            } else if (decision.verdict === 'live') {
              lastValidFrameRef.current = { frame, embedding };
              setPhase('comparing');
              const result = await repo.match(
                embedding,
                accessRoleId,
                matchThresholdProp ?? FACE_MATCH_THRESHOLD_COSINE,
              );

              if (result.employee) {
                const photo = capturePhotoDataUri();
                if (!photo) {
                  setPhase('scanning');
                  setError({ code: 'NO_PHOTO', message: 'Could not capture photo' });
                } else {
                  cleanup();
                  setMatchedEmployee({
                    ...result.employee,
                    lastFramePhoto:     photo,
                    matchedBiometricId: null,
                  });
                  setPhase('matched');
                  return; // exit loop
                }
              } else {
                // Live but no match — reset aggregator so the next scan attempt
                // rebuilds its own multi-frame consensus rather than reusing the
                // frozen 'live' verdict.
                aggregatorRef.current.reset();
                setPhase('scanning');
                setError(null);
              }
            }
          } catch (err) {
            logTiming('[InferEmbedding]', {
              flow:       'auth',
              durationMs: performance.now() - lastFrameTimeRef.current,
              ok:         false,
              errorClass: (err as Error)?.constructor?.name ?? 'Unknown',
            });
            // Slice B: previously-swallowed codes NO_FACE / MULTIPLE_FACES /
            // LIVENESS_BORDERLINE are now surfaced by transitioning to phase
            // 'error' with the corresponding code. ScanningPage's navigation
            // effect propagates the code to /face-error via router state.
            if (err instanceof NoFaceDetectedError) {
              cleanup();
              setPhase('error');
              setError({ code: 'NO_FACE' });
              return;
            }
            else if (err instanceof MultipleFacesError) {
              cleanup();
              setPhase('error');
              setError({ code: 'MULTIPLE_FACES' });
              return;
            }
            else if (err instanceof LowQualityError) {
              // Terminal auth failure — must set phase='error' so ScanningPage navigates to /face-error
              cleanup();
              setPhase('error');
              setError({ code: 'LOW_QUALITY', message: err.reason });
              return;
            }
            else if (err instanceof LivenessBorderlineError) {
              // Slice B: previously silently swallowed. Now surfaced to /face-error
              // via location.state.errorCode = 'LIVENESS_BORDERLINE'.
              // The multi-frame aggregator handles most liveness verdicts, but
              // any residual code path that still throws this error must route
              // to the auth-error page instead of being ignored.
              cleanup();
              setPhase('error');
              setError({ code: 'LIVENESS_BORDERLINE' });
              return;
            }
            else if (err instanceof ModelLoadError) {
              cleanup();
              setPhase('error');
              setError({ code: 'MODEL_LOAD', message: (err as Error).message });
              return;
            }
          }
        }
      }

      if (runningRef.current) {
        rafRef.current = requestAnimationFrame(() => void loop());
      }
    };

    rafRef.current = requestAnimationFrame(() => void loop());
  }, [enabled, accessRoleId, captureFrame, capturePhotoDataUri, cleanup, matchThresholdProp]);

  /**
   * Concurrent quality + liveness gate: runs a 250ms rAF loop where quality checks
   * and liveness direction run SIMULTANEOUSLY every frame. Capture triggers only when
   * BOTH quality is acceptable (hint === null) AND the liveness direction is confirmed
   * at the same frame.
   *
   * Liveness direction is picked on the first frame and resets on a 3s per-direction
   * timeout. A 5-second session timeout fires QUALITY_TIMEOUT if the gate never passes.
   */
  const startQualityGate = useCallback(() => {
    if (!enabled || accessRoleId === null) return;

    const onnx       = OnnxFaceMatcher.getInstance();
    const thresholds = thresholdsProp    ?? SCAN_QUALITY_THRESHOLDS;
    const liveMode   = livenessMdProp    ?? 'minimum';
    const gateTimeout = gateTimeoutProp  ?? QUALITY_GATE_TIMEOUT_MS;

    // Compute detection scale from pre-warm latency (DevicePerfProfile).
    // classifyTier(undefined) → 'medium' → safe 0.75 scale when pre-warm hasn't completed.
    const deviceTier  = classifyTier(onnx.getPrewarmMs());
    const scaleHint   = detectionScaleForTier(deviceTier);

    // Rounds tracking for 'strong' mode (2 directions required).
    // Local to each startQualityGate invocation so retry resets cleanly.
    let livenessRoundsCompleted = 0;
    const livenessRoundsNeeded  = liveMode === 'strong' ? 2 : 1;

    runningRef.current                    = true;
    livenessDirectionRef.current          = null;
    livenessDirectionTimerPausedRef.current = null;
    lastQualityFrameTimeRef.current       = 0;
    analyzingRef.current                  = false;

    setQualityHint(null);
    setLivenessDirection(null);

    // Session timeout for the quality gate
    qualityGateTimeoutRef.current = setTimeout(() => {
      if (runningRef.current) {
        cleanup();
        setPhase('error');
        setError({ code: 'QUALITY_TIMEOUT', message: 'Quality gate session timed out' });
      }
    }, gateTimeout);

    const loop = async () => {
      if (!runningRef.current) return;

      const now = performance.now();
      if (!analyzingRef.current && now - lastQualityFrameTimeRef.current >= nextInterval(lastInferenceMsRef.current)) {
        // NOTE: shouldSkip() was removed here. It produced an infinite-skip lock on
        // slow devices: when lastInferenceMs > SMART_SKIP_THRESHOLD_MS (200), it
        // returned true forever (we never updated the underlying value while
        // skipping). On mobile (lastInferenceMs ≈ 1445ms) this caused every rAF
        // tick to defer indefinitely — the loop "appeared stuck" after the first
        // analysis. nextInterval() already provides correct throttling for slow
        // devices (e.g. 1445ms × 1.2 ≈ 1734ms between analyses); a separate
        // tick-skip is redundant AND buggy.

        lastQualityFrameTimeRef.current = now;
        analyzingRef.current = true;

        const frame = captureFrame();
        if (frame) {
          try {
            const result = await onnx.analyzeQuality(frame, scaleHint, runtimeConfigRef.current);

            // Update adaptive pacing: track how long the last inference took (AQG-2/3)
            lastInferenceMsRef.current = result.durationMs ?? 0;

            if (!runningRef.current) {
              analyzingRef.current = false;
              return;
            }

            // Step 1: compute quality hint (always — independent of liveness)
            const report = result.hasOneFace && result.quality
              ? {
                  blur:         result.quality.blurVar,
                  brightness:   result.quality.brightnessMean,
                  faceSize:     result.quality.faceShortSidePx,
                  faceDetected: true,
                }
              : { blur: 0, brightness: 0, faceSize: 0, faceDetected: false };

            const hint = getQualityHint(report, thresholds);

            // Structured telemetry: gated by DEV || db.settings.debugTelemetry (QGT-1/2/3)
            logTiming('[QualityGate]', {
              durationMs:   result.durationMs,
              ok:           result.ok,
              hasOneFace:   result.hasOneFace,
              faceDetected: report.faceDetected,
              blur:         Math.round(report.blur),
              brightness:   Math.round(report.brightness),
              faceSize:     report.faceSize,
              // Pose data — crucial to understand why waitForFrontal / liveness checks
              // don't transition even when the rest of the gate passes.
              yaw:          result.quality ? Math.round(result.quality.yawDeg)   : null,
              pitch:        result.quality ? Math.round(result.quality.pitchDeg) : null,
              hint,
              thresholds: {
                blurMin:       thresholds.blurThreshold,
                brightnessMin: thresholds.brightnessMin,
                brightnessMax: thresholds.brightnessMax,
                faceMinPx:     thresholds.faceMinPx,
              },
            });

            // Step 2: current pose
            const yaw   = result.quality?.yawDeg   ?? 0;
            const pitch = result.quality?.pitchDeg ?? 0;

            // PRIORITY SYSTEM:
            // Quality checks ALWAYS have priority over liveness instructions.
            // Liveness direction is only shown when quality is good.
            // If quality drops mid-challenge → hide liveness, pause timer, show quality hint.
            // When quality recovers → resume/pick direction, show liveness again.

            if (hint !== null) {
              // Quality issue — show quality hint, hide liveness, pause liveness timer.
              // Pause uses the SAME adaptive budget as the active challenge so resuming
              // doesn't lose the device-proportional grace period.
              const adaptiveTimeoutMs = Math.max(
                thresholds.livenessDirectionTimeoutMs,
                lastInferenceMsRef.current * 4,
              );
              if (livenessDirectionRef.current !== null) {
                // Pause: store remaining time by moving start forward
                const elapsed = performance.now() - livenessChallengeStartRef.current;
                const remaining = adaptiveTimeoutMs - elapsed;
                livenessDirectionTimerPausedRef.current = remaining > 0 ? remaining : 0;
                livenessDirectionRef.current = null; // hide direction prompt
              }
              setQualityHint(hint);
              setLivenessDirection(null);
            } else if (liveMode === 'disabled') {
              // Liveness disabled: quality OK → go straight to scan, no challenge
              logTiming('[ScanPhase]', {
                from:        'quality-gate',
                to:          'scanning',
                via:         'liveness-disabled',
                yaw:         Math.round(yaw),
                pitch:       Math.round(pitch),
              });
              clearTimeout(qualityGateTimeoutRef.current);
              setQualityHint(null);
              setLivenessDirection(null);
              setPhase('scanning');
              startScanLoop();
              analyzingRef.current = false;
              return;
            } else {
              // Adaptive liveness timeout — proportional to actual inference rate so a
              // user on a slow device gets the SAME number of frames of response time
              // as a user on a fast device.
              //   desktop (~150ms inference): max(3000, 600)  = 3000ms  → 20 frames
              //   Samsung A12 (~800ms):       max(3000, 3200) = 3200ms  →  4 frames
              //   Redmi 12C (~1500ms):        max(3000, 6000) = 6000ms  →  4 frames
              // Before this guard, the Redmi 12C user got 3000/1500 ≈ 2 frames to read
              // the prompt, move the head, and have the move captured — basically
              // impossible. The direction would change before any human could respond,
              // producing the false impression that pose detection 'never worked' on
              // mobile.
              const adaptiveTimeoutMs = Math.max(
                thresholds.livenessDirectionTimeoutMs,
                lastInferenceMsRef.current * 4,
              );

              // Quality OK — show liveness direction (minimum or strong mode)
              if (livenessDirectionRef.current === null) {
                // Resume or pick new direction
                const dir = pickDirectionAvoidingPose(yaw, pitch, thresholds);
                livenessDirectionRef.current    = dir;
                // Resume with remaining time (or full timeout if fresh pick)
                const resumeTime = livenessDirectionTimerPausedRef.current ?? adaptiveTimeoutMs;
                livenessChallengeStartRef.current = performance.now() - (adaptiveTimeoutMs - resumeTime);
                livenessDirectionTimerPausedRef.current = null;
              }

              // Liveness direction timeout → pick new direction
              const elapsed = performance.now() - livenessChallengeStartRef.current;
              if (elapsed > adaptiveTimeoutMs) {
                const newDir = pickDirectionAvoidingPose(yaw, pitch, thresholds);
                livenessDirectionRef.current      = newDir;
                livenessChallengeStartRef.current = performance.now();
                livenessDirectionTimerPausedRef.current = null;
              }

              const currentDir = livenessDirectionRef.current!;
              const livenessOk = confirmLivenessDirection(currentDir, yaw, pitch, thresholds);

              // Telemetry — every frame inside the liveness challenge reports the
              // current direction prompt, the pose data SCRFD produced, and whether
              // confirmLivenessDirection accepted it. If you're stuck on this step
              // you'll see e.g. {direction:'right', yaw:-5, ok:false} repeatedly —
              // meaning the user did NOT turn far enough (or yaw is being mis-read,
              // e.g. mirrored camera or wrong rotation).
              logTiming('[LivenessChallenge]', {
                direction:         currentDir,
                yaw:               Math.round(yaw),
                pitch:             Math.round(pitch),
                ok:                livenessOk,
                roundsCompleted:   livenessRoundsCompleted,
                roundsNeeded:      livenessRoundsNeeded,
                elapsedMs:         Math.round(performance.now() - livenessChallengeStartRef.current),
                // Two timeouts: configured (admin modal) and the effective adaptive one
                // applied for THIS device (max of configured vs lastInferenceMs × 4).
                configuredTimeoutMs: thresholds.livenessDirectionTimeoutMs,
                effectiveTimeoutMs:  Math.round(adaptiveTimeoutMs),
                lastInferenceMs:     Math.round(lastInferenceMsRef.current),
              });

              if (livenessOk) {
                livenessRoundsCompleted++;

                if (liveMode === 'strong' && livenessRoundsCompleted < livenessRoundsNeeded) {
                  // Strong mode: more rounds needed → pick dir2 immediately (NO lookFront)
                  const nextDir = pickDirectionAvoidingPose(yaw, pitch, thresholds);
                  livenessDirectionRef.current          = nextDir;
                  livenessChallengeStartRef.current     = performance.now();
                  livenessDirectionTimerPausedRef.current = null;
                  setQualityHint(null);
                  setLivenessDirection(nextDir);
                  analyzingRef.current = false;
                } else {
                  // All rounds done (minimum=1, strong=2) → ask user to look front, then capture
                  livenessDirectionRef.current = null;
                  setLivenessDirection(null);
                  setQualityHint('scan.hint.lookFront'); // final prep before capture
                  analyzingRef.current = false;

                  // Wait for frontal pose on the next frame(s) before triggering capture
                  const waitForFrontal = async () => {
                    if (!runningRef.current) return;
                    const frontFrame = captureFrame();
                    if (frontFrame) {
                      try {
                        const frontResult = await onnx.analyzeQuality(frontFrame, scaleHint, runtimeConfigRef.current);
                        const frontYaw   = frontResult.quality?.yawDeg   ?? 999;
                        const frontPitch = frontResult.quality?.pitchDeg ?? 999;
                        // Frontal limits are slightly tighter than the per-direction
                        // liveness threshold (×0.7 instead of the original ×0.5). The
                        // tighter ×0.5 made slow-device users hold pose for 5+ frames
                        // (≈7s on Redmi 12C) trying to land yaw within ±10°, even right
                        // after they had just confirmed a sideways move. ×0.7 still
                        // keeps the user clearly facing forward without demanding
                        // perfect centering.
                        const yawLimit   = thresholds.yawThresholdDeg * 0.7;
                        const pitchLimit = thresholds.pitchThresholdDeg * 0.7;
                        const isFrontal  = Math.abs(frontYaw) < yawLimit && Math.abs(frontPitch) < pitchLimit;
                        logTiming('[WaitForFrontal]', {
                          yaw:        Math.round(frontYaw),
                          pitch:      Math.round(frontPitch),
                          yawLimit:   Math.round(yawLimit),
                          pitchLimit: Math.round(pitchLimit),
                          isFrontal,
                          durationMs: frontResult.durationMs,
                          hasOneFace: frontResult.hasOneFace,
                        });
                        if (isFrontal) {
                          clearTimeout(qualityGateTimeoutRef.current);
                          setQualityHint(null);
                          setPhase('scanning');
                          startScanLoop();
                          return;
                        }
                      } catch { /* retry */ }
                    }
                    if (runningRef.current) {
                      rafRef.current = requestAnimationFrame(() => void waitForFrontal());
                    }
                  };
                  void waitForFrontal();
                  return;
                }
              } else {
                setQualityHint(null);
                setLivenessDirection(currentDir);
              }
            }

          } catch {
            // Ignore analysis errors — retry on next frame
          } finally {
            analyzingRef.current = false;
          }
        } else {
          analyzingRef.current = false;
        }
      }

      if (runningRef.current) {
        rafRef.current = requestAnimationFrame(() => void loop());
      }
    };

    rafRef.current = requestAnimationFrame(() => void loop());
  }, [enabled, accessRoleId, captureFrame, cleanup, startScanLoop, thresholdsProp, livenessMdProp, gateTimeoutProp]);

  /**
   * Boot sequence for the scan lifecycle.
   *
   * ## Eager Model Loading Strategy (Phase 3)
   * ONNX models are pre-loaded at app startup via `eagerInitializeOnnxModels()` in `main.tsx`
   * (called fire-and-forget before `root.render()`). When the user opens ScanningPage:
   *
   * - If models are already ready (`repo.isReady() === true`): skip the loading phase entirely
   *   → no "loading-models" spinner on first scan (eager-loading fulfilled its goal).
   * - If models are not yet ready (still loading or failed): fall through to `repo.initialize()`
   *   which either awaits the in-progress promise (idempotent) or retries after a failure.
   *
   * @see eagerModelInit.ts — the fire-and-forget loader called in main.tsx
   * @see OnnxFaceMatcher.initialize() — idempotent, concurrent-safe, non-async
   */
  const boot = useCallback(async () => {
    if (!enabled) return;
    setError(null);

    const repo = getFaceMatchingRepository();

    // Skip model initialization if already done (eager-loaded at app boot in main.tsx).
    // This prevents a visible "loading-models" spinner on first scan when models
    // were pre-loaded before React rendered. (T3.2: eager model loading spec)
    if (!repo.isReady()) {
      setPhase('loading-models');
      try {
        await repo.initialize();
      } catch (err) {
        setPhase('error');
        setError({
          code:    'MODEL_LOAD',
          message: err instanceof FaceRecognitionError ? err.message : 'Failed to load ONNX models',
        });
        return;
      }
    }

    setPhase('camera-prompt');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) { cleanup(); return; }
      video.srcObject = stream;
      await video.play();

      setCameraActive(true);
      setPhase('camera-ready');

      // Brief pause before starting loop so first frame is valid
      await new Promise<void>((r) => setTimeout(r, 200));

      if (enableQualityGate) {
        setPhase('quality-checking');
        startQualityGate();
      } else {
        // Feature flag bypass: skip quality gate, go straight to scan loop
        setPhase('scanning');
        startScanLoop();
      }
    } catch (err) {
      const isPermission =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'NotFoundError');
      if (isPermission) {
        setPhase('denied');
        setError({ code: 'CAMERA_DENIED' });
      } else {
        setPhase('error');
        setError({ code: 'CAMERA_ERROR', message: (err as Error).message });
      }
    }
  }, [enabled, enableQualityGate, cleanup, startQualityGate, startScanLoop]);

  const retry = useCallback(() => {
    cleanup();
    retryCounterRef.current += 1;
    setMatchedEmployee(null);
    lastValidFrameRef.current = null;
    // Slice B: reset the aggregator so the retry rebuilds its own multi-frame
    // consensus instead of reusing a frozen terminal verdict from the previous attempt.
    aggregatorRef.current.reset();
    void boot();
  }, [cleanup, boot]);

  useEffect(() => {
    if (!enabled) return;
    void boot();
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, retryCounterRef.current]);

  return {
    phase,
    videoRef,
    cameraActive,
    matchedEmployee,
    lastValidFrameRef,
    error,
    qualityHint,
    livenessDirection,
    retry,
  };
}
